/**
 * Report Studio — the data side: datasets from an analysis, joined or appended tables, filters, aggregation,
 * chart series, cross tables, and the ExportBundle the PDF and Excel writers are given.
 */
import type { BundleChart, BundleItem, BundleLabels, BundleTable, CellValue, ExportBundle, ValueFormat } from './export/bundle';
import { formatValue } from './export/bundle';
import {
  PAGE, PALETTE, REPORT_FORMAT,
  type Aggregation, type ChartVisual, type Dataset, type DataVisualT, type Field, type Filter, type Measure,
  type PivotVisual, type ReportDoc, type SourceDataset, type TableVisual, type Visual,
} from './model';

/* ───────────────────────── values ───────────────────────── */

/** "%4.5" → 4.5, "17219" → 17219, "1,234" → 1234, "Istanbul" → "Istanbul". */
export function parseCell(raw: unknown): CellValue {
  if (raw == null) return null;
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : null;
  if (typeof raw === 'boolean') return raw ? 1 : 0;
  const s = String(raw).trim();
  if (s === '') return null;
  const stripped = s.replace(/^%\s*/, '').replace(/\s*%$/, '').replace(/[\s,]/g, '');
  if (/^[-+]?(\d+\.?\d*|\.\d+)([eE][-+]?\d+)?$/.test(stripped)) return Number(stripped);
  return s;
}

function isPercentText(raw: unknown): boolean {
  return typeof raw === 'string' && /^\s*%\s*[-+]?[\d.,]+\s*$|^\s*[-+]?[\d.,]+\s*%\s*$/.test(raw);
}

/** What a column holds, from its raw values. */
export function detectFormat(raws: unknown[]): ValueFormat {
  let numbers = 0;
  let percents = 0;
  let integers = 0;
  let texts = 0;
  for (const r of raws) {
    if (r == null || r === '') continue;
    if (isPercentText(r)) {
      percents++;
      continue;
    }
    const v = parseCell(r);
    if (typeof v === 'number') {
      numbers++;
      if (Number.isInteger(v)) integers++;
    } else if (v != null) {
      texts++;
    }
  }
  if (percents > 0 && percents >= texts) return 'percent';
  if (numbers > 0 && texts === 0) return integers === numbers ? 'integer' : 'number';
  if (numbers > texts * 4) return integers === numbers ? 'integer' : 'number';
  return 'text';
}

export function cellText(v: CellValue): string {
  if (v == null) return '';
  return typeof v === 'number' ? String(v) : v;
}

const TOTAL_WORDS = new Set(['total', 'toplam', 'genel toplam', 'totaal', 'gesamt', 'summe', 'all', 'tümü']);

/** A row of the analysis that adds up the others ("Total"): its first text value says so. */
export function isTotalRow(row: Record<string, CellValue>, fields: Field[]): boolean {
  for (const f of fields) {
    if (f.key.startsWith('__')) continue; // where an appended row came from, not what it is
    const v = row[f.key];
    if (typeof v === 'string') return TOTAL_WORDS.has(v.trim().toLowerCase());
  }
  return false;
}

/* ───────────────────────── datasets ───────────────────────── */

export interface AnalysisTable {
  id: string;
  title: string;
  layerName: string;
  keys: string[];
  features: Record<string, unknown>[];
  pointName: string;
  travelType: string;
  time: string;
}

/** The analysis' tables as datasets: values parsed, formats found, column names as the user reads them. */
export function datasetsFromAnalysis(tables: AnalysisTable[], columnLabel: (key: string) => string): SourceDataset[] {
  return tables.map((t) => {
    const keys = t.keys.filter((k) => k !== 'geom' && k !== 'the_geom');
    const fields: Field[] = keys.map((k) => ({
      key: k,
      label: columnLabel(k) || k,
      format: detectFormat(t.features.map((f) => f[k])),
    }));
    const rows = t.features.map((f) => {
      const row: Record<string, CellValue> = {};
      for (const k of keys) row[k] = parseCell(f[k]);
      return row;
    });
    return {
      id: t.id,
      kind: 'source',
      title: t.title,
      origin: { point: t.pointName, travel: t.travelType, time: t.time, layer: t.layerName },
      fields,
      rows,
    };
  });
}

export interface Resolved {
  fields: Field[];
  rows: Record<string, CellValue>[];
}

/** The special fields an appended dataset adds. */
export const ORIGIN_FIELDS = ['__table', '__point', '__travel', '__time'] as const;

export function resolveDataset(doc: Pick<ReportDoc, 'datasets'>, id: string, originLabels?: Record<string, string>, seen: Set<string> = new Set()): Resolved {
  const ds = doc.datasets.find((d) => d.id === id);
  if (!ds || seen.has(id)) return { fields: [], rows: [] };
  seen.add(id);
  if (ds.kind === 'source') return { fields: ds.fields, rows: ds.rows };

  const parts = ds.sources
    .map((sid) => ({ ds: doc.datasets.find((d) => d.id === sid), r: resolveDataset(doc, sid, originLabels, new Set(seen)) }))
    .filter((p): p is { ds: Dataset; r: Resolved } => !!p.ds);

  if (ds.kind === 'append') {
    const L = originLabels ?? {};
    const fields: Field[] = [
      { key: '__table', label: L.table ?? 'Table', format: 'text' },
      { key: '__point', label: L.point ?? 'Point', format: 'text' },
      { key: '__travel', label: L.travel ?? 'Travel', format: 'text' },
      { key: '__time', label: L.time ?? 'Time', format: 'text' },
    ];
    const known = new Set(fields.map((f) => f.key));
    const rows: Record<string, CellValue>[] = [];
    for (const p of parts) {
      for (const f of p.r.fields) {
        if (!known.has(f.key)) {
          known.add(f.key);
          fields.push(f);
        }
      }
      const o = p.ds.kind === 'source' ? p.ds.origin : undefined;
      for (const r of p.r.rows) {
        rows.push({
          __table: o?.layer ?? p.ds.title,
          __point: o?.point ?? null,
          __travel: o?.travel ?? null,
          __time: o?.time ?? null,
          ...r,
        });
      }
    }
    return { fields, rows };
  }

  // join: the first table's key values in its order, the others' columns beside them
  const key = ds.key;
  const keyField = parts.map((p) => p.r.fields.find((f) => f.key === key)).find(Boolean) ?? { key, label: key, format: 'text' as ValueFormat };
  const fields: Field[] = [keyField];
  const order: string[] = [];
  const byKey = new Map<string, Record<string, CellValue>>();
  parts.forEach((p, i) => {
    const tag = shortTitle(p.ds);
    for (const f of p.r.fields) {
      if (f.key === key) continue;
      fields.push({ key: `${i}·${f.key}`, label: parts.length > 1 ? `${f.label} · ${tag}` : f.label, format: f.format });
    }
    for (const r of p.r.rows) {
      const k = cellText(r[key]);
      let target = byKey.get(k);
      if (!target) {
        target = { [key]: r[key] };
        byKey.set(k, target);
        order.push(k);
      }
      for (const f of p.r.fields) {
        if (f.key !== key) target[`${i}·${f.key}`] = r[f.key];
      }
    }
  });
  return { fields, rows: order.map((k) => byKey.get(k)!) };
}

function shortTitle(ds: Dataset): string {
  if (ds.kind === 'source' && ds.origin) return ds.origin.layer.replace(/^Catchment/, '') || ds.origin.layer;
  return ds.title;
}

/** Fields every given dataset has: what a join can match on. */
export function commonFields(doc: Pick<ReportDoc, 'datasets'>, ids: string[]): Field[] {
  if (ids.length === 0) return [];
  const all = ids.map((id) => resolveDataset(doc, id).fields);
  return all[0].filter((f) => all.every((fs) => fs.some((g) => g.key === f.key)));
}

/* ───────────────────────── filtering & aggregation ───────────────────────── */

export function applyFilters(r: Resolved, filters: Filter[], excludeTotals: boolean): Record<string, CellValue>[] {
  return r.rows.filter((row) => {
    if (excludeTotals && isTotalRow(row, r.fields)) return false;
    for (const f of filters) {
      if (f.values.length === 0) continue;
      if (!f.values.includes(cellText(row[f.field] ?? null))) return false;
    }
    return true;
  });
}

export function aggregate(values: CellValue[], agg: Aggregation): number | null {
  if (agg === 'count') return values.filter((v) => v != null && v !== '').length;
  if (agg === 'distinct') return new Set(values.filter((v) => v != null && v !== '').map(cellText)).size;
  const nums = values.filter((v): v is number => typeof v === 'number' && Number.isFinite(v));
  if (nums.length === 0) return null;
  switch (agg) {
    case 'sum': return nums.reduce((a, b) => a + b, 0);
    case 'avg': return nums.reduce((a, b) => a + b, 0) / nums.length;
    case 'min': return Math.min(...nums);
    case 'max': return Math.max(...nums);
    case 'first': return nums[0];
    default: return null;
  }
}

/** Percentages are averaged, everything else summed, unless the user says otherwise. */
export function defaultAgg(f: Field | undefined): Aggregation {
  if (!f) return 'sum';
  if (f.format === 'text') return 'count';
  if (f.format === 'percent') return 'avg';
  return 'sum';
}

export function measureFormat(m: Measure, fields: Field[]): ValueFormat {
  if (m.agg === 'count' || m.agg === 'distinct') return 'integer';
  const f = fields.find((x) => x.key === m.field);
  if (!f || f.format === 'text') return 'number';
  if (f.format === 'integer' && m.agg === 'avg') return 'number';
  return f.format;
}

function groupBy(rows: Record<string, CellValue>[], key: string): Map<string, Record<string, CellValue>[]> {
  const out = new Map<string, Record<string, CellValue>[]>();
  for (const r of rows) {
    const k = key ? cellText(r[key] ?? null) : '';
    let list = out.get(k);
    if (!list) out.set(k, (list = []));
    list.push(r);
  }
  return out;
}

/* ───────────────────────── charts ───────────────────────── */

export interface ChartData {
  categories: string[];
  series: { name: string; values: (number | null)[]; color: string }[];
  format: ValueFormat;
  categoryLabel: string;
  valueLabel: string;
  scatterX?: (number | null)[];
  kpi?: { value: number | null; label: string; format: ValueFormat };
}

export interface Words {
  agg: (a: Aggregation) => string;
  of: (agg: string, field: string) => string;
}

const defaultWords: Words = {
  agg: (a) => a,
  of: (agg, field) => `${agg} ${field}`,
};

export function measureLabel(m: Measure, fields: Field[], words: Words = defaultWords): string {
  const f = fields.find((x) => x.key === m.field);
  const name = f?.label ?? m.field;
  if (m.agg === 'first' || m.agg === 'sum') return name;
  return words.of(words.agg(m.agg), name);
}

export function chartData(doc: Pick<ReportDoc, 'datasets'>, v: ChartVisual, words: Words = defaultWords, originLabels?: Record<string, string>): ChartData {
  const r = resolveDataset(doc, v.datasetId, originLabels);
  const rows = applyFilters(r, v.filters, v.excludeTotals);
  const palette = v.palette?.length ? v.palette : PALETTE;
  const measures = v.measures.filter((m) => m.field);
  const fieldLabel = (k: string) => r.fields.find((f) => f.key === k)?.label ?? k;
  const format = measures.length ? measureFormat(measures[0], r.fields) : 'number';

  if (v.chartType === 'kpi') {
    const m = measures[0];
    const value = m ? aggregate(rows.map((x) => x[m.field] ?? null), m.agg) : null;
    return {
      categories: [], series: [], format, categoryLabel: '', valueLabel: '',
      kpi: { value, label: m ? measureLabel(m, r.fields, words) : '', format },
    };
  }

  if (v.chartType === 'scatter') {
    // X = the first measure, Y = the others; one point per category value (or per row)
    const [mx, ...my] = measures;
    const groups = v.category ? groupBy(rows, v.category) : new Map(rows.map((row, i) => [String(i + 1), [row]]));
    const cats = [...groups.keys()];
    const xs = cats.map((c) => (mx ? aggregate(groups.get(c)!.map((x) => x[mx.field] ?? null), mx.agg) : null));
    return {
      categories: cats,
      scatterX: xs,
      series: my.map((m, i) => ({
        name: measureLabel(m, r.fields, words),
        values: cats.map((c) => aggregate(groups.get(c)!.map((x) => x[m.field] ?? null), m.agg)),
        color: palette[i % palette.length],
      })),
      format: my[0] ? measureFormat(my[0], r.fields) : format,
      categoryLabel: mx ? measureLabel(mx, r.fields, words) : '',
      valueLabel: my[0] ? measureLabel(my[0], r.fields, words) : '',
    };
  }

  let categories: string[];
  let series: ChartData['series'];
  let categoryLabel: string;

  if (v.orientation === 'columns') {
    // the measures (the table's columns) along the axis, a series per row / category value
    // the table's own column names along the axis: how a row's values are combined is said once, not per column
    categories = measures.map((m) => fieldLabel(m.field));
    const groups = v.category ? groupBy(rows, v.category) : new Map([['', rows]]);
    series = [...groups.entries()].map(([name, list], i) => ({
      name: name || (v.title || ''),
      values: measures.map((m) => aggregate(list.map((x) => x[m.field] ?? null), m.agg)),
      color: palette[i % palette.length],
    }));
    categoryLabel = '';
  } else {
    const groups = v.category ? groupBy(rows, v.category) : new Map([['', rows]]);
    categories = [...groups.keys()];
    categoryLabel = v.category ? fieldLabel(v.category) : '';
    if (v.series && measures[0]) {
      // a cross table: a series per value of the legend field
      const m = measures[0];
      const names = [...groupBy(rows, v.series).keys()];
      series = names.map((name, i) => ({
        name: name || '—',
        values: categories.map((c) =>
          aggregate(groups.get(c)!.filter((x) => cellText(x[v.series] ?? null) === name).map((x) => x[m.field] ?? null), m.agg)),
        color: palette[i % palette.length],
      }));
    } else {
      // one row a point: how rows are combined does not matter, so the series are named by their columns
      const single = [...groups.values()].every((list) => list.length <= 1);
      series = measures.map((m, i) => ({
        name: single ? fieldLabel(m.field) : measureLabel(m, r.fields, words),
        values: categories.map((c) => aggregate(groups.get(c)!.map((x) => x[m.field] ?? null), m.agg)),
        color: palette[i % palette.length],
      }));
    }
  }

  // sort and top N on the first series
  if ((v.sort !== 'none' || v.topN > 0) && series.length > 0) {
    const idx = categories.map((_, i) => i);
    const first = series[0].values;
    if (v.sort === 'asc') idx.sort((a, b) => (first[a] ?? -Infinity) - (first[b] ?? -Infinity));
    if (v.sort === 'desc') idx.sort((a, b) => (first[b] ?? -Infinity) - (first[a] ?? -Infinity));
    const keep = v.topN > 0 ? idx.slice(0, v.topN) : idx;
    categories = keep.map((i) => categories[i]);
    series = series.map((s) => ({ ...s, values: keep.map((i) => s.values[i]) }));
  }

  const valueLabel = v.series && measures[0] ? measureLabel(measures[0], r.fields, words) : measures.length === 1 ? measureLabel(measures[0], r.fields, words) : '';
  return { categories, series, format, categoryLabel, valueLabel };
}

/* ───────────────────────── tables & cross tables ───────────────────────── */

export function tableData(doc: Pick<ReportDoc, 'datasets'>, v: TableVisual, originLabels?: Record<string, string>): BundleTable {
  const r = resolveDataset(doc, v.datasetId, originLabels);
  const rows = applyFilters(r, v.filters, v.excludeTotals);
  const fields = v.columns.length ? v.columns.map((k) => r.fields.find((f) => f.key === k)).filter((f): f is Field => !!f) : r.fields;
  const totalRows: number[] = [];
  rows.forEach((row, i) => { if (isTotalRow(row, r.fields)) totalRows.push(i); });
  return {
    id: v.id,
    title: v.title,
    columns: fields.map((f) => ({ key: f.key, label: f.label, format: f.format })),
    rows: rows.map((row) => fields.map((f) => row[f.key] ?? null)),
    totalRows,
  };
}

export function pivotData(doc: Pick<ReportDoc, 'datasets'>, v: PivotVisual, words: Words & { total: string }, originLabels?: Record<string, string>): BundleTable {
  const r = resolveDataset(doc, v.datasetId, originLabels);
  const rows = applyFilters(r, v.filters, v.excludeTotals);
  const label = (k: string) => r.fields.find((f) => f.key === k)?.label ?? k;
  const rowKey = (row: Record<string, CellValue>) => v.rows.map((k) => cellText(row[k] ?? null)).join(' · ');
  const colKey = (row: Record<string, CellValue>) => v.columns.map((k) => cellText(row[k] ?? null)).join(' · ');
  const rowKeys: string[] = [];
  const colKeys: string[] = [];
  const cells = new Map<string, CellValue[]>();
  for (const row of rows) {
    const rk = rowKey(row);
    const ck = colKey(row);
    if (!rowKeys.includes(rk)) rowKeys.push(rk);
    if (!colKeys.includes(ck)) colKeys.push(ck);
    const id = `${rk}\u0000${ck}`;
    let list = cells.get(id);
    if (!list) cells.set(id, (list = []));
    list.push(row[v.measure.field] ?? null);
  }
  const fmt = measureFormat(v.measure, r.fields);
  const mLabel = measureLabel(v.measure, r.fields, words);
  const head = v.rows.length ? v.rows.map(label).join(' · ') : mLabel;
  const columns = [
    { key: '__row', label: head, format: 'text' as ValueFormat },
    ...(v.columns.length ? colKeys : ['']).map((c, i) => ({ key: `c${i}`, label: v.columns.length ? (c || '—') : mLabel, format: fmt })),
  ];
  const useCols = v.columns.length ? colKeys : [''];
  const out: CellValue[][] = (v.rows.length ? rowKeys : ['']).map((rk) => [
    rk || (v.rows.length ? '—' : words.total),
    ...useCols.map((ck) => {
      if (!v.rows.length) return aggregate(rows.filter((x) => colKey(x) === ck).map((x) => x[v.measure.field] ?? null), v.measure.agg);
      if (!v.columns.length) return aggregate(rows.filter((x) => rowKey(x) === rk).map((x) => x[v.measure.field] ?? null), v.measure.agg);
      const list = cells.get(`${rk}\u0000${ck}`);
      return list ? aggregate(list, v.measure.agg) : null;
    }),
  ]);
  const totalRows: number[] = [];
  if (v.totals && v.rows.length && v.columns.length) {
    columns.push({ key: '__total', label: words.total, format: fmt });
    out.forEach((line, i) => {
      line.push(aggregate(rows.filter((x) => rowKey(x) === rowKeys[i]).map((x) => x[v.measure.field] ?? null), v.measure.agg));
    });
  }
  if (v.totals && v.rows.length) {
    const totalLine: CellValue[] = [words.total];
    for (const ck of useCols) {
      totalLine.push(aggregate(rows.filter((x) => !v.columns.length || colKey(x) === ck).map((x) => x[v.measure.field] ?? null), v.measure.agg));
    }
    if (v.columns.length) totalLine.push(aggregate(rows.map((x) => x[v.measure.field] ?? null), v.measure.agg));
    totalRows.push(out.length);
    out.push(totalLine);
  }
  // a column with nothing in it (a table without the value field, in an appended dataset) is left out
  const keep = columns.map((c, i) => i === 0 || c.key === '__total' || out.some((line) => line[i] != null));
  return {
    id: v.id,
    title: v.title,
    columns: columns.filter((_, i) => keep[i]),
    rows: out.map((line) => line.filter((_, i) => keep[i])),
    totalRows,
  };
}

/* ───────────────────────── new report ───────────────────────── */

export interface DraftOptions {
  name: string;
  orientation?: 'portrait' | 'landscape';
  map?: ReportDoc['map'];
  mapTitle: string;
}

/** A report as the analysis left it: the map at the top, its tables under it, three to a row. */
/** The most tables a new report puts on its page when the analysis has no catchment summaries. */
const MAX_DRAFT_CARDS = 6;

/** A catchment report's summary table (Catchment Analysis: a row per time, with the total). */
function isSummary(d: SourceDataset): boolean {
  return d.origin?.time === 'Catchment Analysis';
}

export function draftReport(datasets: SourceDataset[], opts: DraftOptions): ReportDoc {
  const orientation = opts.orientation ?? 'landscape';
  const width = PAGE[orientation].width;
  const gap = 16;
  const cols = orientation === 'landscape' ? 3 : 2;
  const cardW = Math.floor((width - gap * (cols - 1)) / cols);
  const cardH = 260;
  const visuals: Visual[] = [];
  const layout: ReportDoc['layout'] = [];
  let top = 0;
  if (opts.map) {
    const id = 'map';
    visuals.push({ id, kind: 'map', title: opts.mapTitle });
    const w = Math.min(width, 640);
    const h = Math.round((w * opts.map.height) / Math.max(1, opts.map.width));
    layout.push({ id, x: 0, y: 0, w, h: Math.min(h + 36, 460) });
    top = Math.min(h + 36, 460) + gap;
  }
  // On the page: the catchment summaries (one row per time) when there are any, else the first few tables. A layer's
  // features - hundreds of rows of hundreds of columns per band - stay in the data list, to be added when wanted.
  const summaries = datasets.filter((d) => isSummary(d));
  const placed = summaries.length > 0 ? summaries : datasets.slice(0, MAX_DRAFT_CARDS);
  placed.forEach((d, i) => {
    const id = `v-${d.id}`;
    visuals.push({ id, kind: 'table', title: d.title, datasetId: d.id, filters: [], excludeTotals: false, columns: [] });
    layout.push({ id, x: (i % cols) * (cardW + gap), y: top + Math.floor(i / cols) * (cardH + gap), w: cardW, h: cardH });
  });
  return {
    format: REPORT_FORMAT,
    kind: 'catchment',
    name: opts.name,
    orientation,
    datasets,
    visuals,
    layout,
    map: opts.map,
    createdAt: new Date().toISOString(),
  };
}

export function newChart(id: string, datasetId: string, fields: Field[], title: string): ChartVisual {
  const text = fields.find((f) => f.format === 'text');
  const nums = fields.filter((f) => f.format !== 'text').slice(0, 3);
  return {
    id, kind: 'chart', title, datasetId, filters: [], excludeTotals: true,
    chartType: 'column',
    category: text?.key ?? '',
    series: '',
    measures: nums.map((f) => ({ field: f.key, agg: defaultAgg(f) })),
    orientation: 'rows', sort: 'none', topN: 0, showLabels: false, palette: PALETTE,
  };
}

export function newPivot(id: string, datasetId: string, fields: Field[], title: string): PivotVisual {
  const texts = fields.filter((f) => f.format === 'text');
  const num = fields.find((f) => f.format !== 'text');
  return {
    id, kind: 'pivot', title, datasetId, filters: [], excludeTotals: true,
    rows: texts[0] ? [texts[0].key] : [],
    columns: texts[1] ? [texts[1].key] : [],
    measure: { field: num?.key ?? texts[0]?.key ?? '', agg: defaultAgg(num) },
    totals: true,
  };
}

/* ───────────────────────── export ───────────────────────── */

export interface BundleOptions {
  include: Set<string>;
  labels: BundleLabels;
  locale: string;
  words: Words & { total: string };
  originLabels: Record<string, string>;
  companyLogo?: string;
  subtitle?: string;
  mapCaption?: string;
  orientation?: 'portrait' | 'landscape';
}

export function toBundle(doc: ReportDoc, o: BundleOptions): ExportBundle {
  const orientation = o.orientation ?? doc.orientation;
  const items: BundleItem[] = [];
  for (const v of doc.visuals) {
    if (!o.include.has(v.id)) continue;
    const p = doc.layout.find((l) => l.id === v.id) ?? { id: v.id, x: 0, y: 1e6, w: 400, h: 300 };
    const base = { id: v.id, x: p.x, y: p.y, w: p.w, h: p.h };
    if (v.kind === 'table') items.push({ ...base, kind: 'table', table: tableData(doc, v, o.originLabels) });
    else if (v.kind === 'pivot') items.push({ ...base, kind: 'table', table: pivotData(doc, v, o.words, o.originLabels) });
    else if (v.kind === 'chart') items.push({ ...base, kind: 'chart', chart: bundleChart(doc, v, o) });
    else if (v.kind === 'map' && doc.map) {
      items.push({ ...base, kind: 'map', image: doc.map.image, imageWidth: doc.map.width, imageHeight: doc.map.height, caption: o.mapCaption });
    } else if (v.kind === 'text') items.push({ ...base, kind: 'text', text: v.text });
  }
  return {
    title: doc.name,
    subtitle: o.subtitle,
    generatedAt: new Date(),
    locale: o.locale,
    companyLogo: o.companyLogo,
    orientation,
    canvasWidth: PAGE[doc.orientation].width,
    items,
    labels: o.labels,
  };
}

function bundleChart(doc: ReportDoc, v: ChartVisual, o: BundleOptions): BundleChart {
  const d = chartData(doc, v, o.words, o.originLabels);
  // a chart made from a table that is in the report sits beside it in Excel
  const sourceTable = doc.visuals.find((t) => t.kind === 'table' && t.datasetId === v.datasetId && o.include.has(t.id));
  return {
    id: v.id,
    title: v.title,
    type: v.chartType,
    categories: d.categories,
    series: d.series,
    format: d.format,
    categoryLabel: d.categoryLabel,
    valueLabel: d.valueLabel,
    showLabels: v.showLabels,
    showLegend: d.series.length > 1 || v.chartType === 'pie' || v.chartType === 'donut',
    scatterX: d.scatterX,
    kpi: d.kpi ? { ...d.kpi } : undefined,
    sourceTableId: sourceTable?.id,
  };
}

/** A value as the studio shows it (re-exported for the canvas). */
export function show(v: CellValue, format: ValueFormat, locale: string): string {
  return formatValue(v, format, locale);
}

/** Every data visual of a dataset, to keep them when a dataset goes. */
export function visualsOf(doc: ReportDoc, datasetId: string): DataVisualT[] {
  return doc.visuals.filter((v): v is DataVisualT => (v.kind === 'table' || v.kind === 'pivot' || v.kind === 'chart') && v.datasetId === datasetId);
}
